import { useCallback, useEffect, useMemo, useState } from 'react';
import { AssetViewerItem, kitAssetViewerItem } from '../../viewer/viewerItems';
import { RevisionCard } from '../assets/revisionCards';
import { useViewerAssetUrl } from '../assets/useViewerAssetUrl';
import { useAssetDeepLink } from '../assets/useAssetDeepLink';
import { KitSlotView } from './kitSlots';

interface ViewerState {
  key: string;
  revision?: number;
}

function viewerItemOf(card: RevisionCard): AssetViewerItem | null {
  const asset = card.asset?.status === 'ready' ? card.asset : card.readyAsset;
  return asset === null ? null : kitAssetViewerItem(asset);
}

/**
 * The asset viewer of the Kit tab (design assets-rethink b1: "A click on a card opens the asset
 * viewer with its revisions"). The open asset lives in the address (`?asset=&rev=`) like the old
 * Assets tab: a refresh or an email link opens it, Back closes it, a link to an asset that is not a
 * keeper of this song loses the value.
 */
export function useKitViewer(input: { accountId: number; catalogItemUuid: string; slots: KitSlotView[] }) {
  const { accountId, catalogItemUuid, slots } = input;
  const cards = useMemo(() => slots.flatMap((one) => one.cards), [slots]);
  const items = useMemo(() => cards.map(viewerItemOf).filter((one): one is AssetViewerItem => one !== null), [cards]);
  const [viewer, setViewer] = useState<ViewerState | null>(null);
  const { show, replace, clear, drop, urlAsset } = useViewerAssetUrl();

  const openCard = useCallback(
    (card: RevisionCard, revision?: number) => {
      const target = viewerItemOf(card);
      if (target !== null) {
        setViewer({ key: target.key, revision });
        show(target.assetUuid, revision);
      }
    },
    [show]
  );
  const openSlot = useCallback(
    (view: KitSlotView) => {
      if (view.readyCard !== null) {
        openCard(view.readyCard);
      }
    },
    [openCard]
  );
  const openFromLink = useCallback(
    (card: RevisionCard, revision?: number) => {
      const target = viewerItemOf(card);
      if (target === null) {
        drop();
        return;
      }
      setViewer((current) => current ?? { key: target.key, revision });
    },
    [drop]
  );
  useAssetDeepLink({ accountId, catalogItemUuid, cards, reels: [], onOpen: openFromLink, onMissing: drop });

  useEffect(() => {
    if (urlAsset === '') {
      setViewer(null);
    }
  }, [urlAsset]);
  const close = useCallback(() => {
    setViewer(null);
    clear();
  }, [clear]);
  const onShownChange = useCallback((next: AssetViewerItem) => replace(next.assetUuid), [replace]);

  return { viewer, items, openSlot, close, onShownChange };
}
